import { useEffect, useState } from "react";
import type { MarketPair, Project } from "../../types/api";
import * as marketService from "../../services/marketService";
import * as projectService from "../../services/projectService";
import { Skeleton } from "../ui/skeleton";

interface WatchlistProps {
  onSelectPair?: (pair: MarketPair) => void;
  selectedSymbol?: string;
}

/**
 * Convertit un projet en MarketPair et enrichit avec les données de marché
 * Retourne null si le symbole n'est pas disponible sur Binance
 */
const projectToMarketPair = async (
  project: Project
): Promise<MarketPair | null> => {
  const symbol = `${project.symbol}/USD`;

  try {
    // Vérifier d'abord si le symbole existe sur Binance
    const exists = await marketService.checkSymbolExists(symbol);
    if (!exists) {
      // Ne pas logger - ce sont des erreurs attendues pour des symboles qui n'existent pas
      return null;
    }

    // Récupérer les données de marché pour ce symbole
    const ticker = await marketService.getTicker(symbol);
    return {
      symbol,
      baseAsset: project.symbol,
      quoteAsset: "USD",
      price: ticker.price,
      change24h: ticker.change24h,
      changePercent24h: ticker.changePercent24h,
      volume24h: ticker.volume24h,
      high24h: ticker.high24h,
      low24h: ticker.low24h,
      projectId: project.id,
      projectName: project.name,
    };
  } catch (error) {
    // Si les données de marché ne sont pas disponibles, ignorer le projet
    console.warn(
      `Données de marché non disponibles pour ${symbol}, projet ignoré:`,
      error
    );
    return null;
  }
};

/**
 * Composant Watchlist - Liste des paires de trading (génériques + projets)
 */
export const Watchlist = ({ onSelectPair, selectedSymbol }: WatchlistProps) => {
  const [pairs, setPairs] = useState<MarketPair[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;

    const loadData = async () => {
      if (!isMounted) return;

      setLoading(true);
      try {
        // Charger les paires génériques et les projets en parallèle
        // Utiliser catch pour éviter que les erreurs bloquent le chargement
        const [genericPairs, projects] = await Promise.all([
          marketService.getPopularPairs().catch((err) => {
            console.warn(
              "Erreur lors du chargement des paires génériques:",
              err
            );
            return [] as MarketPair[];
          }),
          projectService.listProjects().catch(() => [] as Project[]),
        ]);

        if (!isMounted) return;

        // Filtrer uniquement les projets ouverts
        const openProjects = projects.filter((p) => p.status === "open");

        // Convertir les projets en MarketPair et enrichir avec les données de marché
        // Filtrer les null (projets dont le symbole n'existe pas sur Binance)
        const projectPairsResults = await Promise.allSettled(
          openProjects.map((project) => projectToMarketPair(project))
        );
        const projectPairs = projectPairsResults
          .filter(
            (result): result is PromiseFulfilledResult<MarketPair> =>
              result.status === "fulfilled" && result.value !== null
          )
          .map((result) => result.value);

        // Combiner les paires génériques et les projets
        // Les projets sont placés en premier pour les mettre en évidence
        const allPairs = [...projectPairs, ...genericPairs];

        if (isMounted) {
          setPairs(allPairs);
        }
      } catch (error) {
        console.error("Erreur lors du chargement de la watchlist:", error);
        if (isMounted) {
          setPairs([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadData();

    // Rafraîchir toutes les 60 secondes
    intervalId = setInterval(() => {
      if (isMounted) {
        void loadData();
      }
    }, 60000);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col rounded-2xl border border-border bg-card bg-gradient-to-br from-card to-muted/30 p-4 shadow-md shadow-slate-900/5 backdrop-blur-sm dark:border-white/5 dark:from-slate-900/50 dark:to-slate-800/30 dark:shadow-lg dark:shadow-black/20">
      <h3 className="mb-4 text-lg font-semibold text-foreground dark:text-white">Watchlist</h3>
      <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto pr-2">
        {pairs.map((pair) => {
          const isSelected = selectedSymbol === pair.symbol;
          const isPositive = pair.changePercent24h >= 0;

          return (
            <button
              key={pair.symbol}
              onClick={() => onSelectPair?.(pair)}
              className={`rounded-lg border p-3 text-left transition-all duration-200 ${
                isSelected
                  ? "border-cyan-500/40 bg-cyan-500/10 shadow-md shadow-cyan-500/10 dark:border-cyan-400/50 dark:bg-cyan-400/10 dark:shadow-cyan-400/10"
                  : "border-border bg-muted/40 backdrop-blur-sm hover:bg-muted dark:border-white/5 dark:bg-white/5 dark:hover:bg-white/10 dark:hover:shadow-sm"
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-foreground dark:text-white">{pair.symbol}</p>
                  <p className="text-xs text-muted-foreground dark:text-white/60">
                    {pair.projectName || `${pair.baseAsset}/${pair.quoteAsset}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold tabular-nums text-foreground dark:text-white">
                    {pair.price.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 8,
                    })}
                  </p>
                  <p
                    className={`text-xs ${
                      isPositive ? "text-emerald-400/90" : "text-rose-400/90"
                    }`}>
                    {isPositive ? "+" : ""}
                    {pair.changePercent24h.toFixed(2)}%
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
